import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Validation } from '../../api/types.js';

const MAX_COMMENT = 4000;

interface Props {
  validation: Validation;
  busy: boolean;
  onDecide: (payload: { decision: 'accept' | 'refuse'; comment: string | null }) => void;
  onClose: () => void;
}

export function DecideModal({ validation, busy, onDecide, onClose }: Props) {
  const [decision, setDecision] = useState<'accept' | 'refuse'>('accept');
  const [comment, setComment] = useState('');
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    taRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const trimmed = comment.trimEnd();
  // Per `feedback_validation_workflow` (CFO memo): refuse REQUIRES a
  // comment, accept the comment is optional.
  const submitDisabled =
    busy || (decision === 'refuse' && trimmed.length === 0) || trimmed.length > MAX_COMMENT;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitDisabled) return;
    onDecide({ decision, comment: trimmed.length > 0 ? trimmed : null });
  }

  return (
    <div
      className="modal__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="decide-modal"
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="decide-modal-title">
        <header className="modal__head">
          <h2 id="decide-modal-title" className="display modal__title">
            Validation · {validation.primaryKey.split('__').slice(-1)[0]}
          </h2>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Fermer"
            data-testid="decide-close"
          >
            ×
          </button>
        </header>

        <form className="dispute-form" onSubmit={handleSubmit}>
          <div className="muted" style={{ fontSize: 12 }}>
            Demande de{' '}
            {validation.requesterDisplayName ??
              validation.requesterEmail ??
              validation.requesterUid}
            {validation.requestComment ? (
              <blockquote className="decide-request-comment">
                « {validation.requestComment} »
              </blockquote>
            ) : null}
          </div>

          <div className="decide-choice" role="radiogroup" aria-label="Décision">
            <label
              className={`decide-choice__option${decision === 'accept' ? ' is-active' : ''}`}
              data-testid="decide-accept-pill"
            >
              <input
                type="radio"
                name="decision"
                value="accept"
                checked={decision === 'accept'}
                onChange={() => setDecision('accept')}
              />
              <span>Accepter</span>
            </label>
            <label
              className={`decide-choice__option${decision === 'refuse' ? ' is-active' : ''}`}
              data-testid="decide-refuse-pill"
            >
              <input
                type="radio"
                name="decision"
                value="refuse"
                checked={decision === 'refuse'}
                onChange={() => setDecision('refuse')}
              />
              <span>Refuser</span>
            </label>
          </div>

          <label className="dispute-form__label" htmlFor="decide-comment">
            <span className="eyebrow">
              {decision === 'refuse'
                ? 'Commentaire (obligatoire pour un refus) *'
                : 'Commentaire (optionnel)'}
            </span>
            <textarea
              id="decide-comment"
              ref={taRef}
              className="comments-composer__textarea"
              placeholder={
                decision === 'refuse'
                  ? 'Expliquez pourquoi vous refusez cette facture…'
                  : 'Justification ou validation explicite…'
              }
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={MAX_COMMENT + 100}
              rows={3}
              data-testid="decide-comment"
            />
            <span className="muted comments-composer__counter">
              {trimmed.length}/{MAX_COMMENT}
            </span>
          </label>

          <footer className="modal__foot dispute-form__foot">
            <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
              Annuler
            </button>
            <button
              type="submit"
              className={decision === 'refuse' ? 'btn-primary' : 'btn-primary'}
              disabled={submitDisabled}
              data-testid="decide-submit"
            >
              {busy ? 'Envoi…' : decision === 'accept' ? 'Accepter' : 'Refuser'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
